// 保護 API クライアント。アクセストークンを Authorization: Bearer で付与し、
// 401 でトークンをクリア（onUnauthorized）して再ログインへ。403/5xx は ApiError に正規化。
// SPA は cognito:groups / custom:tenant_id をローカル判定の根拠にしない（サーバ側が正本, R1/R5）。

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export interface ApiClientDeps {
  readonly baseUrl: string;
  readonly getAccessToken: () => string | null;
  readonly onUnauthorized: () => void;
  readonly fetchImpl?: typeof fetch;
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
}

export function createApiClient(deps: ApiClientDeps): ApiClient {
  const doFetch = deps.fetchImpl ?? fetch;

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = deps.getAccessToken();
    if (token === null || token === "") {
      throw new ApiError(401, "No access token");
    }
    const headers = new Headers();
    headers.set("authorization", `Bearer ${token}`);
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers.set("content-type", "application/json");
      init.body = JSON.stringify(body);
    }

    const res = await doFetch(`${deps.baseUrl}${path}`, init);

    if (res.status === 401) {
      deps.onUnauthorized();
      throw new ApiError(401, "Unauthorized");
    }
    if (!res.ok) {
      throw new ApiError(res.status, `Request failed with status ${res.status}`);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  return {
    get: <T>(path: string) => request<T>("GET", path),
    post: <T>(path: string, body: unknown) => request<T>("POST", path, body),
    put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
    del: <T>(path: string) => request<T>("DELETE", path),
  };
}
